import React, { useEffect, useState } from 'react';
import MainLayout from '../../components/layouts/MainLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import { useToast } from '../../components/common/Toast';
import { adminService } from '../../services/admin.service';
import { theme } from '../../config/theme';
import { formatDate } from '../../utils/helpers';
import { InboxIcon } from '../../components/icons/Icons';
import Pagination from '../../components/common/Pagination';

const ContactMessages = () => {
  const toast = useToast();
  const [messages, setMessages] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [isUnreadOnly, setIsUnreadOnly] = useState(false);

  const fetchMessages = async (nextPage = 1) => {
    setLoading(true);
    try {
      const response = await adminService.getContactMessages(nextPage, 20, isUnreadOnly ? true : null);
      const payload = response?.data?.data || response?.data;

      setMessages(payload?.messages || []);
      setPagination(payload?.pagination || null);
    } catch (error) {
      console.error('Failed to load contact messages:', error);
      toast.error('Failed to load contact messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages(page);
  }, [page, isUnreadOnly]);

  const getStatusBadge = (isRead) => {
    if (isRead) return <Badge variant="success">Read</Badge>;
    return <Badge variant="warning">Unread</Badge>;
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      await adminService.markContactMessageRead(messageId);
      setMessages(prev => prev.map(m => (m.id === messageId ? { ...m, is_read: true } : m)));
      toast.success('Marked as read');
    } catch (error) {
      console.error('Failed to mark as read:', error);
      toast.error('Failed to update message status');
    }
  };

  const handleDelete = async (messageId) => {
    if (!confirm('Are you sure you want to delete this message? This action cannot be undone.')) {
      return;
    }

    try {
      await adminService.deleteContactMessage(messageId);
      setMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success('Message deleted successfully');
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast.error('Failed to delete message');
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <LoadingSpinner fullScreen />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div style={{ padding: theme.spacing.xl, maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ marginBottom: theme.spacing.xl, display: 'flex', flexWrap: 'wrap', gap: theme.spacing.lg, alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{ fontSize: theme.typography.fontSize['3xl'], fontWeight: theme.typography.fontWeight.bold, marginBottom: theme.spacing.xs }}>
              Contact Messages
            </h1>
            <p style={{ color: theme.colors.text.secondary }}>
              {pagination?.total || 0} total messages{isUnreadOnly ? ' (unread only)' : ''}
            </p>
          </div>

          <div style={{ display: 'flex', gap: theme.spacing.md, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant={isUnreadOnly ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => {
                setPage(1);
                setIsUnreadOnly(true);
              }}
            >
              Unread
            </Button>
            <Button
              variant={!isUnreadOnly ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => {
                setPage(1);
                setIsUnreadOnly(false);
              }}
            >
              All
            </Button>
          </div>
        </div>

        {messages.length === 0 ? (
          <EmptyState
            icon={<InboxIcon size={48} />}
            title={isUnreadOnly ? 'No unread messages' : 'No contact messages yet'}
            description={isUnreadOnly ? 'Everything has been reviewed' : 'When users submit the contact form, messages will appear here'}
          />
        ) : (
          <div style={{ display: 'grid', gap: theme.spacing.lg }}>
            {messages.map((m) => (
              <Card key={m.id} padding="xl">
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: theme.spacing.lg, flexWrap: 'wrap' }}>
                  <div>
                    <h2 style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.bold, marginBottom: theme.spacing.sm }}>
                      {m.subject}
                    </h2>
                    <div style={{ color: theme.colors.text.secondary, fontSize: theme.typography.fontSize.sm, marginBottom: theme.spacing.md }}>
                      From <span style={{ color: theme.colors.text.primary, fontWeight: theme.typography.fontWeight.medium }}>{m.name}</span> ({m.email}) · {formatDate(m.created_at, 'long')}
                    </div>
                  </div>
                  <div>{getStatusBadge(m.is_read)}</div>
                </div>

                <p style={{ color: theme.colors.text.secondary, lineHeight: 1.8, marginTop: theme.spacing.md }}>
                  {m.message}
                </p>

                <div style={{ marginTop: theme.spacing.xl, display: 'flex', gap: theme.spacing.sm }}>
                  {!m.is_read && (
                    <Button variant="outline" size="sm" onClick={() => handleMarkAsRead(m.id)}>
                      Mark as read
                    </Button>
                  )}
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    onClick={() => handleDelete(m.id)}
                  >
                    Delete
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {pagination && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            hasNextPage={pagination.hasNextPage}
            hasPreviousPage={pagination.hasPreviousPage}
            totalItems={pagination.total}
            itemsPerPage={pagination.limit}
            onPageChange={(next) => setPage(next)}
          />
        )}
      </div>
    </MainLayout>
  );
};

export default ContactMessages;

